/**
 * AdoraCare -- all-source lead inbox: pure view module
 * (ALL-SOURCE-LEAD-INBOX-V1, B3 / Story 2.2).
 *
 * All decision logic and copy live here as pure exports (the org-intake /
 * workboard view-module idiom) so they are unit-testable via
 * react-dom/server + node without a browser.
 *
 * LABELS RULE (locked design item 5): every source / stage / status /
 * aging / filter-option display string RIDES THE WIRE from the backend's
 * ONE shared display-label helper (lib/lead-inbox-display-labels.ts).
 * This module maps NO vocabulary token to copy -- it renders the wire
 * labels verbatim, so per-org label overrides stay a config-only change.
 * Only page CHROME (section titles, filter-group names, empty-state copy)
 * is local.
 *
 * ACCESS DECISION: the frontend guard REUSES decideOrgIntakeAccess (the
 * routable-role superset; org-intake-view.ts) -- the backend owner-config
 * gate on GET /api/lead-inbox is the only real authority; a routable role
 * that is NOT the configured owner gets the endpoint's 401, rendered as
 * the honest access-restricted state. No /me flag, no feature flag
 * (internal surface, ADR-0016).
 */

import type { DashboardMe } from '@/dashboard/types';
import { clinicLabelOf } from '@/dashboard/clinic-context';

// ---- Backend contract mirrors (frontend-local per the CRM convention) ----

export type LeadInboxPhoneAvailability = 'available' | 'no_linked_phone';
export type LeadInboxClinicState = 'pending' | 'assigned';
export type LeadInboxChipKey =
  | 'clinic_pending'
  | 'needs_action'
  | 'unassigned'
  | 'worked'
  | 'appointment'
  | 'stale'
  | 'closed'
  | 'identity_converted';
export type LeadInboxAgingBucket = 'fresh' | 'today' | 'aging' | 'stale';

export interface LeadInboxChip {
  readonly key: LeadInboxChipKey;
  readonly label: string;
}

export interface LeadInboxRow {
  readonly leadId: number;
  readonly name: string | null;
  readonly sourceNormalized: string;
  readonly sourceLabel: string;
  readonly clinicState: LeadInboxClinicState;
  readonly clinicId: number | null;
  readonly clinicLabel: string | null;
  readonly stage: string;
  readonly stageLabel: string;
  readonly caseStatus: string | null;
  readonly chips: readonly LeadInboxChip[];
  readonly ownerRole: string | null;
  readonly ownerLabel: string;
  readonly needsAction: boolean;
  readonly createdAt: string | null;
  readonly ageMinutes: number | null;
  readonly agingBucket: LeadInboxAgingBucket | null;
  readonly agingLabel: string | null;
  readonly isStale: boolean;
  readonly phoneAvailability: LeadInboxPhoneAvailability;
  readonly detailInCallerScope: boolean;
  readonly isOrgPending: boolean;
}

export interface LeadInboxClinicOption {
  readonly clinicId: number;
  readonly clinicLabel: string | null;
}

export interface LeadInboxSelectOption {
  readonly value: string;
  readonly label: string;
}

export interface LeadInboxResult {
  readonly role: DashboardMe['role'];
  readonly ownerRole: string;
  readonly rows: readonly LeadInboxRow[];
  readonly totalMatched: number;
  readonly offset: number;
  readonly limit: number;
  readonly hasMore: boolean;
  readonly scanComplete: boolean;
  readonly staleMinutes: number;
  readonly agingBucketBoundaries: readonly [number, number, number];
  readonly clinics: readonly LeadInboxClinicOption[];
  readonly sourceOptions: readonly LeadInboxSelectOption[];
  readonly agingOptions: readonly LeadInboxSelectOption[];
  readonly clinicStateOptions: readonly LeadInboxSelectOption[];
  readonly needsActionOptions: readonly LeadInboxSelectOption[];
  readonly darkSources: readonly string[];
}

// ---- The five filters (locked design item 4 -- exactly these) ----

export interface LeadInboxFilters {
  readonly clinicState: string;
  readonly source: string;
  readonly needsAction: string;
  readonly clinicId: string;
  readonly aging: string;
}

export const LEAD_INBOX_DEFAULT_FILTERS: LeadInboxFilters = Object.freeze({
  clinicState: 'all',
  source: 'all',
  needsAction: 'all',
  clinicId: 'all',
  aging: 'all',
});

/** Query-string params for fetchLeadInbox ('all' filters are omitted). */
export function leadInboxQueryParams(
  filters: LeadInboxFilters,
  offset: number,
): Record<string, string> {
  const params: Record<string, string> = {};
  if (filters.clinicState !== 'all') params.clinicState = filters.clinicState;
  if (filters.source !== 'all') params.source = filters.source;
  if (filters.needsAction !== 'all') params.needsAction = filters.needsAction;
  if (filters.clinicId !== 'all') params.clinicId = filters.clinicId;
  if (filters.aging !== 'all') params.aging = filters.aging;
  if (offset > 0) params.offset = String(offset);
  return params;
}

// ---- Copy (honest empty / ceiling / hint constants -- page CHROME only) ----

export const LEAD_INBOX_TITLE = 'Lead inbox -- every source, one queue';

export const LEAD_INBOX_HINT =
  'Every captured lead in this organization, newest first, with its honest status. Clinic-pending rows are assigned in Org intake; assigned rows open in the lead workspace.';

export const LEAD_INBOX_EMPTY_TITLE = 'No leads match this view.';

/**
 * DARK-SOURCE HONESTY (locked design item 6, verbatim): shown when the
 * inbox is empty while the WhatsApp / webchat capture flags are off. NO
 * sample or fake rows, ever.
 */
export const LEAD_INBOX_DARK_SOURCE_COPY =
  'Web form and walk-in leads appear now. WhatsApp and webchat leads will appear after their go-live ladders are enabled.';

export const LEAD_INBOX_EMPTY_HINT =
  'Adjust the filters, or wait for new leads to arrive.';

export const LEAD_INBOX_UNAVAILABLE_COPY =
  'The lead inbox is unavailable right now.';

export const LEAD_INBOX_RESTRICTED_COPY =
  'This inbox is worked by another role in your organization (set in Routing settings).';

/**
 * Honest bound note (the org-intake truncation-copy lesson: never claim
 * more rows exist when the page might simply be full).
 */
export const LEAD_INBOX_SCAN_CAPPED_NOTE =
  'Showing the newest leads only -- the scan hit its bound, so older leads and the match count are incomplete. Narrow the filters to see further back.';

/** Truthful count label (lower-bound-honest when the scan was capped). */
export function matchedCountLabel(
  totalMatched: number,
  scanComplete: boolean,
): string {
  const noun = totalMatched === 1 ? 'lead' : 'leads';
  return scanComplete
    ? `${totalMatched} ${noun}`
    : `${totalMatched}+ ${noun} (bounded scan)`;
}

/** '(No name)' is the honest label for a nameless lead. */
export const LEAD_INBOX_NO_NAME_LABEL = '(No name)';

/** Clinic label for a filter option / row, id-fallback honest. */
export function clinicOptionLabel(option: LeadInboxClinicOption): string {
  return clinicLabelOf(option.clinicLabel);
}

// ---- Link targets (compose EXISTING surfaces; no dead links) ----

/**
 * The EXISTING Lead 360 detail route. Offered ONLY when the server says
 * the row's clinic is inside the caller's verified scope
 * (row.detailInCallerScope -- the B2 no-dead-links rule) AND the
 * workspace surface is enabled for this user (me.leadsWorkspaceEnabled).
 */
export function leadInboxDetailHref(row: LeadInboxRow): string | null {
  if (!row.detailInCallerScope || row.clinicId === null) return null;
  return `/dashboard/leads/${encodeURIComponent(String(row.leadId))}?clinicId=${encodeURIComponent(String(row.clinicId))}`;
}

/** Clinic-pending rows link into the EXISTING org-intake set-clinic flow. */
export const LEAD_INBOX_ORG_INTAKE_HREF = '/dashboard/org-intake';

// ---- Error mapping (reuses the org-intake status-code convention) ----

function errorStatus(error: unknown): number | null {
  if (
    error !== null &&
    typeof error === 'object' &&
    'status' in error &&
    typeof (error as { status: unknown }).status === 'number'
  ) {
    return (error as { status: number }).status;
  }
  return null;
}

/** True when an inbox LOAD failure is the backend access gate (401). */
export function isLeadInboxAccessRestricted(error: unknown): boolean {
  return errorStatus(error) === 401;
}
